import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import type { LibraryMessage } from '../lib/content';
import { apiFetch } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { StandardsAccessGate } from '../components/StandardsAccessGate';

const PAGE_SIZE = 40;
export function MessagesPage() {
  const { isAuthenticated, sessionToken } = useAuth();
  const [params, setParams] = useSearchParams();
  const standard = params.get('standard') ?? '';
  const query = params.get('q') ?? '';
  const [messages, setMessages] = useState<LibraryMessage[] | null>(null);
  const [error, setError] = useState('');
  const [page, setPage] = useState(0);
  useEffect(() => {
    document.title = 'Message libraries · AstdZ';
    setMessages(null);
    setError('');
    if (!isAuthenticated) return;
    const controller = new AbortController();
    apiFetch<LibraryMessage[]>('/v1/content/messages', { signal: controller.signal }).then(setMessages).catch((err: Error) => {
      if (!controller.signal.aborted) setError(err.message);
    });
    return () => controller.abort();
  }, [isAuthenticated, sessionToken]);
  const codes = useMemo(() => [...new Set(messages?.map((message) => message.standard))], [messages]);
  const filtered = useMemo(() => messages?.filter((message) => (!standard || message.standard === standard) && `${message.refHex} ${message.title} ${message.categoryName} ${message.standard}`.toLowerCase().includes(query.toLowerCase().trim())) ?? [], [messages, standard, query]);
  useEffect(() => setPage(0), [standard, query]);
  function update(key: string, value: string) {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value); else next.delete(key);
    setParams(next, { replace: true });
  }
  if (!isAuthenticated) return <StandardsAccessGate title="Message libraries" />;
  return <div className="docs-content standards-library">
    <div className="crumb">AstdZ / documentation / message libraries</div>
    <h1>Find the right message.</h1>
    <p>Search the documented messages across all eleven standards. Reference codes are scoped to a standard. The JavaScript reference codec can encode and decode the family; message libraries remain draft specifications.</p>
    <div className="library-filters"><label>Standard<select className="form-input" value={standard} onChange={(event) => update('standard', event.target.value)}><option value="">All standards</option>{codes.map((code) => <option key={code}>{code}</option>)}</select></label>
      <label>Search messages<input className="form-input" type="search" placeholder="Message, category, or hex reference" value={query} onChange={(event) => update('q', event.target.value)} /></label></div>
    {error && <p role="alert">{error}</p>}
    {!messages && !error && <p role="status">Loading message libraries…</p>}
    {messages && <p className="text-muted" role="status">{filtered.length} matching messages of {messages.length} documented</p>}
    <div className="message-results">{filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE).map((message) => <article className="message-result" key={`${message.standard}-${message.ref}`}>
      <div><Link to={`/docs/standards/${message.standard.toLowerCase()}`}>{message.standard}</Link> <code>{message.refHex}</code><span className="text-muted"> · Severity {message.severity}</span></div>
      <h2>{message.title}</h2><p className="text-muted">{message.categoryName}{message.flags ? ` · ${message.flags}` : ''}</p>
    </article>)}</div>
    {messages && filtered.length === 0 && <p>No messages match these filters.</p>}
    {filtered.length > PAGE_SIZE && <nav className="content-pagination" aria-label="Message result pages"><button className="btn" disabled={page === 0} onClick={() => setPage((value) => value - 1)}>← Previous</button><span>Page {page + 1} of {Math.ceil(filtered.length / PAGE_SIZE)}</span><button className="btn" disabled={(page + 1) * PAGE_SIZE >= filtered.length} onClick={() => setPage((value) => value + 1)}>Next →</button></nav>}
  </div>;
}
